import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";
import { Box, Grid, Link, Paper, Skeleton, Switch, Typography } from "@mui/material";
import { actions as AppActions } from "../../../App/Ducks/App.duck";
import { MESSAGE_SEVERITY_ERROR } from "../../../../common";
import employerService from "../../Services/EmployerService";
import CreateEmployerForm from "../../Forms/CreateEmployerForm";
import CreateSubEmployerForm from "../../Forms/CreateSubEmployerForm";
import { normalizeError } from "app/utils";
import { useNavigate } from "react-router-dom";
import { useBreadcrumb } from "app/components/Breadcrumbs";
import { default as MuiTextField } from "@mui/material/TextField";
import { useOwnerGetDetailQuery } from "app/hooks/api/owner";
import { useEmployersSearchQuery } from "app/hooks/api/employer";
import { Uuid } from "../../../../utils";
import {
  AutocompleteStyled,
  ButtonStyled,
  DividerStyled,
  FormLabelStyled,
  PaperStyled,
  TypographyDescriptionStyled,
  TypographyQuestionStyled,
  TypographySubEmployerHeaderStyled
} from "./EmployerAddPage.styles";
const EmployerAddPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ownerId = useSelector((state) => state.AppReducer.environment.ownerId);
  const userId = useSelector((state) => state.AppReducer.user.userId);
  const [employer, setEmployer] = useState({
    employerId: Uuid.newV4(),
    employerName: "",
    employerEmail: "",
    employerPhone: "",
    internalNumber: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    chamberOfCommerce: "",
    sectorCode: "",
    payrollTaxNumber: "",
    address: "",
    zipCode: "",
    city: "",
    numberOfEmployees: 0,
    ownerId,
    subEmployers: [],
    parentId: null
  });
  const [employerIsHolding, setEmployerIsHolding] = useState(false);
  const [employerIsSubEmployer, setEmployerIsSubEmployer] = useState(false);
  const [employersTotalCount, setEmployersTotalCount] = useState({});
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [selectedParent, setSelectedParent] = useState(null);
  const { data: ownerDetail } = useOwnerGetDetailQuery({
    variables: { ownerId }
  });
  const owners = useMemo(() => ownerDetail ? [ownerDetail] : [], [ownerDetail]);
  const subOwners = useMemo(() => ownerDetail?.subOwners ?? [], [ownerDetail]);
  const { data: employersHoldingData } = useEmployersSearchQuery({
    variables: { page: 1, pageSize: 9999 },
    options: { enabled: employerIsSubEmployer }
  });
  useEffect(() => {
    if (ownerDetail) {
      setSelectedOwner(ownerDetail);
    }
  }, [ownerDetail]);
  useEffect(() => {
    const loadTotalCount = async () => {
      try {
        const response = await employerService.getOwnerEmployersTotalCount(userId);
        setEmployersTotalCount(response.data);
      } catch (error) {
        const severity = MESSAGE_SEVERITY_ERROR;
        const details = {
          method: "getEmployersTotalCount",
          severity
        };
        dispatch(AppActions.displayMessage(details));
      }
    };
    loadTotalCount();
  }, [dispatch, userId]);
  const saveEmployer = useCallback(async (values) => {
    return employerService.createEmployer(values);
  }, []);
  const handleChange = useCallback(
    (field, value) => {
      const newEmployer = { ...employer };
      newEmployer[field] = value;
      setEmployer(newEmployer);
    },
    [employer]
  );
  const handleChangeParent = useCallback(
    (parent) => {
      setEmployer({
        ...employer,
        parentId: parent?.employerId
      });
    },
    [employer]
  );
  const handleCancel = useCallback(() => {
    navigate("../");
  }, [navigate]);
  const handleCreateEmployer = useCallback(async () => {
    saveEmployer(employer).then(() => navigate("../")).catch((err) => {
      const errors = normalizeError(err);
      const severity = MESSAGE_SEVERITY_ERROR;
      const details = {
        method: "createEmployer",
        severity,
        message: errors.message
      };
      dispatch(AppActions.displayMessage(details));
    });
  }, [dispatch, navigate, employer, saveEmployer]);
  const handleSwitchEmployerIsHolding = useCallback(() => {
    if (!employerIsHolding && !employer.subEmployers.length) {
      employer.subEmployers.push({
        employerId: Uuid.newV4(),
        employerName: "",
        employerEmail: "",
        employerPhone: "",
        internalNumber: "",
        contactName: "",
        contactEmail: "",
        contactPhone: "",
        chamberOfCommerce: "",
        sectorCode: "",
        payrollTaxNumber: "",
        address: "",
        zipCode: "",
        city: "",
        numberOfEmployees: 0,
        ownerId: employer.ownerId,
        parentId: employer.employerId
      });
    } else {
      employer.subEmployers = [];
    }
    setEmployerIsHolding(!employerIsHolding);
    setEmployerIsSubEmployer(false);
  }, [employer, employerIsHolding]);
  const handleSwitchEmployerIsSubEmployer = useCallback(() => {
    setEmployerIsHolding(false);
    setEmployerIsSubEmployer(!employerIsSubEmployer);
    handleChangeParent(null);
    setSelectedParent(null);
  }, [employerIsSubEmployer, handleChangeParent]);
  const handleClickAddAnother = useCallback(() => {
    employer.subEmployers.push({
      employerId: Uuid.newV4(),
      employerName: "",
      employerEmail: "",
      employerPhone: "",
      internalNumber: "",
      contactName: "",
      contactEmail: "",
      contactPhone: "",
      chamberOfCommerce: "",
      sectorCode: "",
      payrollTaxNumber: "",
      address: "",
      zipCode: "",
      city: "",
      ownerId,
      parentId: employer.employerId
    });
  }, [employer, ownerId]);
  const handleDeleteSubEmployer = useCallback(
    (employerId) => {
      const newSubEmployers = employer.subEmployers.filter((se) => se.employerId !== employerId);
      setEmployer({
        ...employer,
        subEmployers: newSubEmployers
      });
    },
    [employer]
  );
  const title = useMemo(() => {
    return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Typography, { variant: "h5" }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.add" })), /* @__PURE__ */ React.createElement(Typography, { variant: "subtitle2", textTransform: "uppercase" }, typeof employersTotalCount?.total !== "undefined" ? /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.totalCount", values: { totalCount: employersTotalCount.total } }) : /* @__PURE__ */ React.createElement(Skeleton, { width: 200 })));
  }, [employersTotalCount]);
  useBreadcrumb("employers.add", {
    hideTrail: true,
    titleElement: title
  });
  const parentIdAutoComplete = useMemo(() => {
    const employerOwnerId = employer.ownerId;
    const reducedOptions = (employersHoldingData?.items ?? []).filter((item) => {
      if (item.parentId !== null) return false;
      if (!item.ownerId) return true;
      return item.ownerId === employerOwnerId;
    }).sort((a, b) => a.employerName.localeCompare(b.employerName));
    const ListboxComponent = React.forwardRef(function ListboxComponent2(props, ref) {
      const { ...rest } = props;
      return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("ul", { ref, ...rest }), /* @__PURE__ */ React.createElement(TypographyDescriptionStyled, { variant: "body1" }, /* @__PURE__ */ React.createElement(
        FormattedMessage,
        {
          id: "employers.add.subEmployers.holding.description",
          values: { b: (chunks) => /* @__PURE__ */ React.createElement("b", null, chunks), br: /* @__PURE__ */ React.createElement("br", null) }
        }
      )));
    });
    return /* @__PURE__ */ React.createElement(
      AutocompleteStyled,
      {
        name: "parentId",
        value: selectedParent,
        options: reducedOptions,
        getOptionLabel: (option) => {
          return option.employerName;
        },
        disableClearable: true,
        isOptionEqualToValue: (item, current) => {
          if (typeof current === "string") {
            return item.value === current;
          }
          return item.employerId === current.employerId;
        },
        onChange: (e, value) => {
          setSelectedParent(value);
          handleChangeParent(value);
        },
        renderInput: (params) => /* @__PURE__ */ React.createElement(MuiTextField, { ...params, name: "parentId", variant: "outlined", fullWidth: true }),
        ListboxComponent: (listboxProps) => /* @__PURE__ */ React.createElement(ListboxComponent, { ...listboxProps })
      }
    );
  }, [employersHoldingData, handleChangeParent, selectedParent, employer.ownerId]);
  return /* @__PURE__ */ React.createElement(Paper, { elevation: 0, square: true }, /* @__PURE__ */ React.createElement(Grid, { container: true, spacing: 3 }, /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 6 }, /* @__PURE__ */ React.createElement(PaperStyled, { isInfo: true }, /* @__PURE__ */ React.createElement(
    CreateEmployerForm,
    {
      employer,
      employerIsHolding: false,
      employerShowHolding: false,
      onEmployerChange: handleChange,
      owners,
      setEmployersHolding: () => {
      },
      setSubOwners: () => {
      },
      subOwners,
      setSelectedOwner,
      selectedOwner,
      setSelectedParent,
      setEmployer
    }
  ))), /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 6 }, /* @__PURE__ */ React.createElement(PaperStyled, null, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(TypographyQuestionStyled, { variant: "body1" }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.holding.question", values: { b: (chunks) => /* @__PURE__ */ React.createElement("b", null, chunks) } })), /* @__PURE__ */ React.createElement(FormLabelStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.holding" })), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Switch, { checked: employerIsHolding, onChange: handleSwitchEmployerIsHolding }))), !employerIsHolding && /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(TypographyQuestionStyled, { variant: "body1" }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.holdingPart.question", values: { b: (chunks) => /* @__PURE__ */ React.createElement("b", null, chunks) } })), /* @__PURE__ */ React.createElement(FormLabelStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.holdingPart" })), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Switch, { checked: employerIsSubEmployer, onChange: handleSwitchEmployerIsSubEmployer }))), !!employerIsHolding && /* @__PURE__ */ React.createElement(TypographySubEmployerHeaderStyled, { variant: "body1" }, /* @__PURE__ */ React.createElement(
    FormattedMessage,
    {
      id: "employers.add.subEmployers.description",
      values: { totalCount: employersTotalCount.total }
    }
  )), !!employerIsHolding && !!employer.subEmployers.length && employer.subEmployers.map((subEmployer, subEmployerIndex) => /* @__PURE__ */ React.createElement(Grid, { container: true, key: subEmployerIndex }, /* @__PURE__ */ React.createElement(
    CreateSubEmployerForm,
    {
      employer,
      subEmployer,
      onDelete: handleDeleteSubEmployer
    }
  ), /* @__PURE__ */ React.createElement(DividerStyled, null))), !!employerIsHolding && /* @__PURE__ */ React.createElement(
    Link,
    {
      sx: { textAlign: "right", width: "100%" },
      href: "EmployerAddPage#",
      onClick: handleClickAddAnother,
      style: { display: "none" }
    },
    /* @__PURE__ */ React.createElement(Typography, { variant: "h6" }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.add.subEmployers.button" }))
  ), !!employerIsSubEmployer && /* @__PURE__ */ React.createElement(Grid, { container: true, spacing: 2 }, /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 6 }, /* @__PURE__ */ React.createElement(FormLabelStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.add.subEmployers.holding" })), parentIdAutoComplete)))), /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 12 }, /* @__PURE__ */ React.createElement(Box, { textAlign: "right" }, /* @__PURE__ */ React.createElement(ButtonStyled, { onClick: handleCancel, variant: "outlined" }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "button.cancel" })), /* @__PURE__ */ React.createElement(
    ButtonStyled,
    {
      disabled: !employer.employerName || !employer.ownerId || !employer.zipCode || !employer.city || !employer.address,
      onClick: handleCreateEmployer,
      variant: "contained"
    },
    /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.add.createNewEmployer" })
  )))));
};
export default EmployerAddPage;
